import type { SolverStrategy, StrategyInput, StrategyOutput } from '../types';
import type { TileStackModel } from '../../boardMVC/models/board/TileStackModel';
import { TileColor } from '../../boardMVC/TileColor';
import MathUtils from '../../../utils/MathUtils';
import { adjacentMatchBlockerTypes } from '../../boardMVC/models/board/MatchSequencerMCV';
import { StrategyMakeMatch } from './StrategyMakeMatch';
import { BOARD_SETTINGS } from '../../scene/GameSceneConstants';

export class StrategyPrioritizeV2 implements SolverStrategy {
  public readonly label = 'prioritizeV2';

  private readonly randomStrategyFallback = new StrategyMakeMatch();

  public getNextMove(input: StrategyInput): StrategyOutput {
    const { boardModel, trayModel } = input;

    const trayItems = trayModel.getItems();

    // Get all empty stacks
    const emptyStacks = boardModel.getEmptyStacks();

    if (emptyStacks.length === 0) {
      return this.randomStrategyFallback.getNextMove(input);
    }

    // Evaluate all possible moves with composite scoring
    const scoredMoves = this.evaluateAllMoves(emptyStacks, trayItems);

    if (scoredMoves.length === 0) {
      return this.randomStrategyFallback.getNextMove(input);
    }

    // Select the best move based on composite score
    const bestMove = this.selectBestScoredMove(scoredMoves);

    return {
      fromStack: bestMove.fromStackIndex,
      toStack: { x: bestMove.stack.x, y: bestMove.stack.y },
    };
  }

  private evaluateAllMoves(
    emptyStacks: TileStackModel[],
    trayItems: TileStackModel[],
  ): {
    stack: TileStackModel;
    fromStackIndex: number;
    score: number;
  }[] {
    return trayItems
      .map((trayItem, trayIndex) => {
        if (!trayItem.hasTiles()) return [];
        return emptyStacks.map((emptyStack) => ({
          stack: emptyStack,
          fromStackIndex: trayIndex,
          score: this.getMatchScore(trayItem, emptyStack),
        }));
      })
      .flat();
  }

  private getMatchScore(trayStack: TileStackModel, emptySpace: TileStackModel): number {
    const topColor = trayStack.getTopmostColor();
    const secondaryColor = trayStack.getNextCoveredColor();

    let matchScore = 0;

    if (topColor !== TileColor.None) {
      const matchingConnections = emptySpace.getMatchingConnections(topColor, (s) => s.isBlocked());

      const matchingCount = matchingConnections.reduce((acc, s) => acc + s.getMatchingColorCount(topColor), 0);
      const willMakeMatch = matchingCount >= BOARD_SETTINGS.matchLength;

      matchScore += matchingConnections.length * 100;

      if (willMakeMatch) {
        matchScore *= 2;

        // TODO check if counting amount will improve it
        const willHitBlocker = matchingConnections.some((s) =>
          s
            .getConnectedStacks()
            .some((neighbour) => neighbour.blocker && adjacentMatchBlockerTypes.includes(neighbour.blocker?.getType())),
        );
        if (willHitBlocker) {
          matchScore *= 2;
        }

        const willHitUnderTileBlocker = matchingConnections.some((s) => s.underTileBlocker);

        if (willHitBlocker && !willHitUnderTileBlocker) {
          matchScore *= 2;
        }
      }
    }

    if (trayStack.getTileCount() >= 2 && secondaryColor !== TileColor.None) {
      const connections = emptySpace.getMatchingConnections(secondaryColor, (s) => s.isBlocked());
      matchScore += connections.length * 50; // Medium weight for secondary color matches
    }

    return matchScore;
  }

  private selectBestScoredMove(
    scoredMoves: {
      stack: TileStackModel;
      fromStackIndex: number;
      score: number;
    }[],
  ): {
    stack: TileStackModel;
    fromStackIndex: number;
    score: number;
  } {
    // Sort by composite score (highest first)
    scoredMoves.sort((a, b) => b.score - a.score);

    // Get the highest score
    const maxScore = scoredMoves[0].score;

    // Filter moves with the highest score
    const bestMoves = scoredMoves.filter((move) => move.score === maxScore);

    // Randomize among equally good moves
    return MathUtils.rnd.pick(bestMoves);
  }
}
